import { filter, find, flatMap, get, includes } from 'lodash';
import { DateTime } from 'luxon';
import { getCurrentClubSeasons } from '../../clubs/selectors';

export const isLoading = (state, teamId) => {
  const loading = get(state, 'teams.teamSquad.currentlyLoading');
  return includes(loading, teamId);
};

export const hasLoaded = (state, teamId) => {
  const loaded = get(state, 'teams.teamSquad.hasLoaded');
  return includes(loaded, teamId);
};

export const getLoadingError = (state, teamId) =>
  find(get(state, 'teams.teamSquad.loadingError'), { teamId });

export const getPlayersByTeamId = (state, teamId) =>
  get(state, `teams.teamSquad.players.${teamId}`, []);

export const getStaffAndCoachesByTeamId = (state, teamId) =>
  get(state, `teams.teamSquad.staffAndCoaches.${teamId}`, []);

export const getProfile = (state, teamId, profileId) =>
  get(state, `teams.teamSquad.profiles[${teamId}-${profileId}]`);

export const getProfileSeasons = (state, teamId, profileId, seasonId) => {
  const clubSeasons = getCurrentClubSeasons(state);
  const profile = getProfile(state, teamId, profileId);
  if (!profile || !profile.joined) {
    return clubSeasons;
  }

  const joined = DateTime.fromISO(profile.joined);
  const left = profile.left ? DateTime.fromISO(profile.left) : null;
  return filter(clubSeasons, (season) => {
    if (season.id === seasonId) {
      // Always return currently selected season
      return true;
    }

    const seasonEnd = DateTime.fromISO(season.end);
    if (!left) {
      // Player hasn't left the club so include all seasons from when they joined
      return seasonEnd >= joined;
    }

    // Player has left the club so include only seasons during their stint
    const seasonStart = DateTime.fromISO(season.start);
    return seasonStart <= left && seasonEnd >= joined;
  });
};

export const getProfileStats = (state, teamId, profileId, seasonId) =>
  get(state, `teams.teamSquad.profileStats[${teamId}-${profileId}-${seasonId}]`);

export const getProfileFixtures = (state, teamId, profileId, seasonId) =>
  get(state, `teams.teamSquad.profileFixtures[${teamId}-${profileId}-${seasonId}]`);

export const getProfileLoadingError = (state, teamId, profileId, seasonId) =>
  find(get(state, 'teams.teamSquad.profileLoadingErrors'), {
    id: `${teamId}-${profileId}-${seasonId}`,
  });

export const isLoadingProfile = (state, teamId, profileId, seasonId) => {
  const loading = get(state, 'teams.teamSquad.loadingProfiles');
  return includes(loading, `${teamId}-${profileId}-${seasonId}`);
};

export const hasLoadedProfile = (state, teamId, profileId, seasonId) => {
  const loaded = get(state, 'teams.teamSquad.loadedProfiles');
  return includes(loaded, `${teamId}-${profileId}-${seasonId}`);
};

export const getTeamPlayers = (state, teamId) => {
  const teamPlayers = getPlayersByTeamId(state, teamId);
  return flatMap(teamPlayers, (group) => group.members);
};

export const getTeamCoaches = (state, teamId) => {
  const teamCoaches = getStaffAndCoachesByTeamId(state, teamId);
  return flatMap(teamCoaches, (group) => group.members);
};
